import React, { useState, useEffect, useRef } from 'react';
import Images from '../Images';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaUserCircle, FaShoppingCart, FaSearch, FaChevronDown, FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaShoppingBag, FaTrashAlt, FaSignOutAlt, FaQuestionCircle } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import tw from 'tailwind-styled-components';
import { Remove_User, Set_Cart_Modal_Open } from '../Redux/actions';
import LocationModal from './LocationModal';
import LoginModal from '../Pages/User/Login';
import EmailLoginModal from './EmailLoginModal';
import SignupModal from '../Pages/User/Register';
import axios from 'axios';
import { toast } from 'react-toastify';
import Config from '../Config'
// import Cart from './Cart';
import useAddress from '../hooks/useAddress';
import useCart from '../hooks/useCart';
import { NavbarCategoriesLoading } from './SkeletonLoading/NavbarCategoriesLoading';

// const baseUrl = process.env.REACT_APP_API_BASE_URL;
const baseUrl = Config.apiUrl;
const categoriesEndpoint = Config.apiUrl;
const Navbar = ({ categories: initialCategories }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const user = useSelector(state => state.UserReducer.user);
    // const isCartModalOpen = useSelector((state) => state.CartReducer.isCartModalOpen);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [categories, setCategories] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [isLocationLoading, setIsLocationLoading] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isOTPLoginModalOpen, setIsOTPLoginModalOpen] = useState(false);
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [locationError, setLocationError] = useState('');
    const dropdownRef = useRef(null);
    const [themeImageUrl, setThemeImageUrl] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const { addresses, fetchUserAddresses } = useAddress();

    // Fetch cart data to display badge count
    const { data: cartData } = useCart();

    // Calculate total items
    const cartItems = cartData?.items || [];
    const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);

    const handleSearch = () => {
        if (searchQuery.trim()) {
            // Navigate to the AllProducts page with type 'search' and the query param
            navigate(`/products/search?q=${encodeURIComponent(searchQuery.trim())}`);
        } else {
            toast.info("Please enter a search term");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchUserAddresses();
        }
    }, [user, fetchUserAddresses]);

    // Update location when addresses are loaded
    useEffect(() => {
        if (addresses && addresses.length > 0) {

            // Find default address or fallback to the first one
            const defaultAddress = addresses.find(addr => addr.is_default) || addresses[0];

            if (defaultAddress) {
                const addressParts = [
                    defaultAddress.address_line_1,
                    defaultAddress.address_line_2,
                    defaultAddress.city,
                    defaultAddress.state,
                    defaultAddress.postal_code
                    // defaultAddress.country
                ].filter(Boolean); // Remove empty values

                const locationString = addressParts.join(', ');

                // Update the state and local storage if a valid address is found
                if (locationString && locationString !== userLocation) {
                    saveLocationToStorage(locationString);
                }
            }
        }
    }, [addresses]); // Re-run when addresses change

    // load saved location from localStorage
    useEffect(() => {
        const savedLocation = localStorage.getItem('userLocation');
        if (savedLocation) {
            setUserLocation(savedLocation);
        }
    }, []);

    // save location to localStorage
    const saveLocationToStorage = (location) => {
        localStorage.setItem('userLocation', location);
        setUserLocation(location);
    };

    // clear saved location
    const clearLocation = () => {
        localStorage.removeItem('userLocation');
        setUserLocation(null);
        // handleEnableLocation();
    };

    // get location name from coordinates
    const getLocationFromCoordinates = async (latitude, longitude) => {
        try {
            setIsLocationLoading(true);
            // OpenStreetMap API 
            const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
            );

            if (response.data && response.data.address) {
                const address = response.data.address;
                const city = address.city || address.town || address.village || address.suburb || '';
                const state = address.state || address.region || '';
                const country = address.country || '';

                const locationString = `${city}${state ? ', ' + state : ''}${country ? ', ' + country : ''}`;
                saveLocationToStorage(locationString || 'Unknown Location');
            } else {
                saveLocationToStorage('Location detected');
            }
        } catch (error) {
            console.error('Error getting location name:', error);
            saveLocationToStorage('Location detected');
        } finally {
            setIsLocationLoading(false);
        }
    };

    // handle location selection from search
    const handleLocationSelection = (locationString) => {
        setLocationError('');
        setUserLocation(locationString);
    };

    const handleEnableLocation = () => {
        if (navigator.geolocation) {
            setIsLocationLoading(true);
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    // console.log('User Location:', { latitude, longitude });

                    setLocationError('');
                    await getLocationFromCoordinates(latitude, longitude);
                    setIsModalOpen(false);
                },
                (error) => {
                    console.error("Error getting location:", error);
                    let errorMessage = "Unable to retrieve your location.";
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = "You have blocked location access. Please enable it in your browser settings.";
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = "Your location is currently unavailable. Please ensure your device's location service is turned on.";
                            break;
                        case error.TIMEOUT:
                            errorMessage = "Could not get your location in time. Please try again.";
                            break;
                        default:
                            errorMessage = "An unknown error occurred while fetching your location.";
                            break;
                    }
                    setLocationError(errorMessage);
                    setIsLocationLoading(false);
                    // setIsModalOpen(false);
                }
            );
        } else {
            // toast.error("Geolocation is not supported by this browser.");
            setLocationError("Your browser does not support geolocation.");
            setIsLocationLoading(false);
        }
    };

    const handleOpenLoginModal = () => {
        setIsLoginModalOpen(true);
        setIsSignupModalOpen(false);
    };

    const handleOpenSignupModal = () => {
        setIsSignupModalOpen(true);
        setIsLoginModalOpen(false);
    };

    const handleCloseAuthModals = () => {
        setIsLoginModalOpen(false);
        setIsOTPLoginModalOpen(false);
        setIsSignupModalOpen(false);
    };

    const handleSwitchToSignup = () => {
        setIsLoginModalOpen(false);
        setIsOTPLoginModalOpen(false);
        setIsSignupModalOpen(true);
    };

    const handleSwitchToLogin = () => {
        setIsSignupModalOpen(false);
        setIsOTPLoginModalOpen(false);
        setIsLoginModalOpen(true);
    };

    const handleProfileClick = () => {
        navigate('/profile');
        setIsProfileDropdownOpen(false);
    };

    const handleLogout = () => {
        dispatch(Remove_User());
        setIsProfileDropdownOpen(false);
        navigate('/');
        toast.success('Logged out successfully!');
    };

    const handleMyOrders = () => {
        // Navigate to orders page
        navigate('/orders');
        setIsProfileDropdownOpen(false);
    };

    const handleHelpSupport = () => {
        navigate('/help');
        setIsProfileDropdownOpen(false);
    }

    const handleDeleteAccount = () => {
        // Handle account deletion when implemented
        // console.log('Delete Account clicked');
        setIsProfileDropdownOpen(false);
    };

    const handleLoginSuccess = () => {
        handleCloseAuthModals();
        toast.success('Login successful!');
    };

    const handleSignupSuccess = () => {
        handleCloseAuthModals();
        toast.success('Signup successful!');
    };

    const handleTabClick = (category, categoryObj = null) => {
        setActiveTab(category.toLowerCase());

        // Get category_id for the selected category
        let categoryId = null;
        if (category.toLowerCase() !== 'all' && categoryObj) {
            // if (categoryObj) {
            categoryId = categoryObj.id;
        }

        //dispatch event to notify Dashboard & Home of category change
        const categoryEvent = new CustomEvent('categoryChanged', {
            detail: {
                category: category.toLowerCase(),
                categoryId: categoryId
            }
        });
        window.dispatchEvent(categoryEvent);
    };

    const updateScrollButtons = (element) => {
        const maxScrollLeft = element.scrollWidth - element.clientWidth;
        setCanScrollLeft(element.scrollLeft > 0);
        setCanScrollRight(element.scrollLeft < maxScrollLeft);
    };

    // scroll buttons for categories
    const handleScroll = (direction) => {
        const container = document.getElementById('categories-container');
        const scrollAmount = 700;

        if (direction === 'left') {
            container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        } else {
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }

        setTimeout(() => updateScrollButtons(container), 300);
    };

    const handleScrollEvent = (e) => {
        updateScrollButtons(e.target);
    };

    useEffect(() => {
        const fetchTheme = async () => {
            try {
                const response = await axios.get(`${baseUrl}/themes`);
                // console.log('response of theme is >> ', response.data.data);
                const themes = response.data.data;

                // Find the active theme
                const activeTheme = themes.find(theme => theme.is_active === true);

                if (activeTheme) {
                    // console.log("activeTheme id is >> ", activeTheme.id);
                    // console.log('url validate >> >> ', activeTheme.image_url);

                    if (activeTheme.image_url) {
                        setThemeImageUrl(activeTheme.image_url);
                    } else {
                        setThemeImageUrl(''); // fallback or default
                    }
                } else {
                    setThemeImageUrl(''); // fallback or default
                }
            } catch (err) {
                console.error('Error fetching theme:', err);
            }
        };

        fetchTheme();
    }, []);

    useEffect(() => {
        // show loading if no categories
        if (!initialCategories || initialCategories.length === 0) {
            setCategoriesLoading(true);
            return;
        }
        const activeCategories = initialCategories
        .filter(category => category.is_active)
        .sort((a, b) => a.sort_order - b.sort_order);

        setCategoryData(activeCategories);
        
        const categoryNames = activeCategories.map(category => category.name);
            // const allCategories = ['All', ...categoryNames];
            // setCategories(allCategories);
            setCategories(categoryNames);

        // Set active tab based on URL
        const currentPath = location.pathname.substring(1);
        if (currentPath && categoryNames.some(cat => cat.toLowerCase() === currentPath.toLowerCase())){
        setActiveTab(currentPath.toLowerCase());
        } else {
        setActiveTab('all');
        }

    setCategoriesLoading(false);
    }, [initialCategories, location.pathname]);

    useEffect(() => {
        const container = document.getElementById('categories-container');
        if (container) {
            updateScrollButtons(container);
            container.addEventListener('scroll', handleScrollEvent);

            const handleResize = () => updateScrollButtons(container);
            window.addEventListener('resize', handleResize);

            return () => {
                container.removeEventListener('scroll', handleScrollEvent);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [categories]);

    const handleOpenCartModal = () => {      // open cart modal if logged in, else open login modal
        if (user) {
            dispatch(Set_Cart_Modal_Open(true));
        } else {
            setIsLoginModalOpen(true);
            // toast.info("Please login to view your cart.");
        }
    };

    const handleCloseCartModal = () => {
        dispatch(Set_Cart_Modal_Open(false));
    };

    useEffect(() => {
        const handleClickOutside = (event) => { // close dropdown if clicked outside
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsProfileDropdownOpen(false);
            }
        };

        if (isProfileDropdownOpen) { // only when dropdown is open
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isProfileDropdownOpen]);

    return (
        <>
            <MainNavbarWrapper style={{
                backgroundImage: themeImageUrl ? `url(${themeImageUrl})` : 'none',
                height: '180px',
            }}>
                <NavContainer>
                    <LogoContainer>
                        <Link to="/">
                            <Logo src={Images.Logo} alt="logo" />
                        </Link>
                    </LogoContainer>

                    <LocationSelector onClick={() => setIsModalOpen(true)}>
                        <FaMapMarkerAlt className="w-4 h-4 text-white" />
                        <LocationText>
                            {isLocationLoading ? 'Getting location...' : (userLocation || 'Select Location')}
                        </LocationText>
                        <FaChevronDown className="w-3 h-3 lg:w-4 lg:h-4 text-white" />
                    </LocationSelector>

                    {/* Desktop Search */}
                    <SearchContainer>
                        <SearchWrapper>
                            <FaSearch className="absolute left-3 w-5 h-5 text-kuality-blue" />
                            <SearchInput
                                type="text"
                                placeholder='Search for your favorite brands and products...'
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </SearchWrapper>
                    </SearchContainer>

                    <NavActions>
                        {/* Authentication Section */}
                        {user ? (
                            <ProfileDropdownContainer ref={dropdownRef}>
                                {/* Toggle Profile Dropdown */}
                                <NavButton onClick={() => setIsProfileDropdownOpen(prev => !prev)}>
                                    <FaUserCircle className="w-6 h-6" />
                                    <NavButtonText>
                                        Profile
                                    </NavButtonText>
                                </NavButton>

                                {/* Profile Dropdown */}
                                {isProfileDropdownOpen && (
                                    <DropdownMenu>
                                        {/* User Info Header */}
                                        <UserInfoHeader>
                                            <UserInfoContent>
                                                <UserAvatar>
                                                    <FaUserCircle className="w-6 h-6 text-gray-500" />
                                                </UserAvatar>
                                                <UserDetails>
                                                    <UserName>
                                                        <span className='text-kuality-blue'>Hi </span>{`${user.first_name}` || 'User'}
                                                    </UserName>
                                                    <UserPhone>
                                                        {user.phone}
                                                    </UserPhone>
                                                </UserDetails>
                                            </UserInfoContent>
                                        </UserInfoHeader>

                                        {/* Menu Items */}
                                        <DropdownMenuItems>
                                            <DropdownButton onClick={handleProfileClick}>
                                                <FaUserCircle className="w-5 h-5" />
                                                <span>My Account</span>
                                            </DropdownButton>

                                            <DropdownButton onClick={handleMyOrders}>
                                                <FaShoppingBag className="w-5 h-5" />
                                                <span>My Orders</span>
                                            </DropdownButton>

                                            {user.role === Config.userType.SELLER ? (
                                                <DropdownLink
                                                    href={Config.sellerDomain}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <FaUserCircle className="w-5 h-5" />
                                                    <span>Seller Dashboard</span>
                                                </DropdownLink>
                                            ) : (
                                                // Show "Become a Supplier" if buyer
                                                <DropdownLink
                                                    href={`${Config.sellerDomain}/#/register`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <FaUserCircle className="w-5 h-5" />
                                                    <span>Become a Supplier</span>
                                                </DropdownLink>
                                            )}

                                            <DropdownButton onClick={handleHelpSupport}>
                                                <FaQuestionCircle className="w-5 h-5" />
                                                <span>Help & Support</span>
                                            </DropdownButton>

                                            <DropdownDivider />
                                            <DropdownButton onClick={handleLogout}>
                                                <FaSignOutAlt className="w-5 h-5" />
                                                <span>Logout</span>
                                            </DropdownButton>
                                        </DropdownMenuItems>
                                    </DropdownMenu>
                                )}
                            </ProfileDropdownContainer>
                        ) : (
                            <NavButton onClick={handleOpenLoginModal}>
                                <FaUserCircle className="w-6 h-6" />
                                <NavButtonText>Login</NavButtonText>
                            </NavButton>
                        )}

                        <NavButton onClick={handleOpenCartModal}>
                            <div className="relative">
                                <FaShoppingCart className="w-6 h-6" />
                                {totalItems > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border border-white">
                                        {totalItems}
                                    </span>
                                )}
                            </div>
                            <NavButtonText>Cart</NavButtonText>
                        </NavButton>
                    </NavActions>
                </NavContainer>

                {/* Mobile Location Row*/}
                <MobileLocationContainer>
                    <MobileLocationSelector onClick={() => setIsModalOpen(true)}>
                        <FaMapMarkerAlt className="w-4 h-4 text-white" />
                        <MobileLocationText>
                            {isLocationLoading ? 'Getting location...' : (userLocation || 'Select Delivery Location')}
                        </MobileLocationText>
                        <FaChevronRight className="w-3 h-3 text-white" />
                    </MobileLocationSelector>
                </MobileLocationContainer>

                {/* Mobile Search Row */}
                <MobileSearchContainer>
                    <MobileSearchWrapper>
                        <FaSearch className="absolute left-3 w-5 h-5 text-kuality-blue" />
                        <SearchInput
                            type="text"
                            placeholder='Search for your favorite brands and products...'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </MobileSearchWrapper>
                </MobileSearchContainer>
            </MainNavbarWrapper>

            {/* Modals */}
            {isModalOpen && <LocationModal
                setOpenModal={setIsModalOpen}
                onEnableLocation={handleEnableLocation}
                isLoading={isLocationLoading}
                currentLocation={userLocation}
                onClearLocation={clearLocation}
                onLocationSelect={handleLocationSelection}
                error={locationError}
                clearError={() => setLocationError('')}
            />}

            <EmailLoginModal
                isOpen={isLoginModalOpen}
                onClose={handleCloseAuthModals}
                onSwitchToSignup={handleSwitchToSignup}
                onSwitchToEmailLogin={() => {
                    setIsLoginModalOpen(false);
                    setIsOTPLoginModalOpen(true);
                }}
                onLoginSuccess={handleLoginSuccess}
            />

            <LoginModal
                isOpen={isOTPLoginModalOpen}
                onClose={() => setIsOTPLoginModalOpen(false)}
                onSwitchToSignup={handleSwitchToSignup}
                onLoginSuccess={handleLoginSuccess}
            />

            <SignupModal
                isOpen={isSignupModalOpen}
                onClose={handleCloseAuthModals}
                onSwitchToLogin={handleSwitchToLogin}
                onSignupSuccess={handleSignupSuccess}
            />
            {/* <Cart isOpen={isCartModalOpen} onClose={handleCloseCartModal} /> */}

            {/* Categories */}
            <CategoriesContainer>
                {categoriesLoading ? (
                    <NavbarCategoriesLoading />
                ) : (
                    <>
                        {/* Left scroll button */}
                        {canScrollLeft && (
                            <ScrollButton
                                $position="left"
                                onClick={() => handleScroll('left')}
                                aria-label="Scroll left"
                            >
                                <FaChevronLeft className="w-2 h-2 sm:w-3 sm:h-3 text-kuality-blue" />
                            </ScrollButton>
                        )}

                        <CategoriesScrollContainer
                            id="categories-container"
                            style={{
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            }}
                        >
                            <CategoriesList>
                                {categories.map((category, index) => {
                                    // find the corresponding category object for ID lookup
                                    const categoryObj = category.toLowerCase() !== 'all'
                                        ? categoryData.find(cat => cat.name.toLowerCase() === category.toLowerCase())
                                        : null;
                                    // const categoryObj = categoryData.find(cat => cat.name.toLowerCase() === category.toLowerCase());

                                    // Find the category with ID 5001 specifically for the "All" tab image
                                    const allCategoryImageObj = categoryData.find(cat => cat.id === 5001);
                                    const allCategoryImageUrl = allCategoryImageObj?.image;

                                    return (
                                        <CategoryLink
                                            key={index}
                                            to={category.toLowerCase() === 'all' 
                                                ? '/' 
                                                : `/products/all-products?category_id=${categoryObj?.id}&title=${encodeURIComponent(category)}`
                                            }
                                            onClick={() => handleTabClick(category, categoryObj)}
                                            $active={activeTab === category.toLowerCase()}
                                        >
                                            {category.toLowerCase() === 'all' ? (
                                                // for "All" category, use the image from ID 5001 
                                                <CategoryImages src={allCategoryImageUrl || Images.KualitySignTransparent} alt="All Categories" />
                                            ) :
                                                categoryObj && categoryObj.image ? (
                                                    <CategoryImages src={categoryObj.image} alt={category.name} />
                                                ) : (
                                                    <CategoryImages src={Images.KualitySignTransparent} alt={category.name} />
                                                )}

                                            <CategoryText>{category}</CategoryText>
                                        </CategoryLink>
                                    );
                                })}
                            </CategoriesList>
                        </CategoriesScrollContainer>

                        {/* Right scroll button */}
                        {canScrollRight && (
                            <ScrollButton
                                $position="right"
                                onClick={() => handleScroll('right')}
                                aria-label="Scroll right"
                            >
                                <FaChevronRight className="w-2 h-2 sm:w-3 sm:h-3 text-kuality-dark" />
                            </ScrollButton>
                        )}
                    </>
                )}
            </CategoriesContainer>
        </>
    )
}

// Styled Components

// Main Navigation Wrapper
// const MainNavbarWrapper = tw.div`
//     w-full bg-red-500 shadow-md
// `;
// const MainNavbarWrapper = tw.div`
//     w-full shadow-md
//     bg-cover bg-center bg-no-repeat
// `;
const MainNavbarWrapper = tw.div`
  w-full shadow-md
  bg-cover bg-center bg-no-repeat
  bg-kuality-blue
`;



const NavContainer = tw.nav`
    flex items-center justify-between p-3 w-full
`;

const LogoContainer = tw.div`
    flex items-center flex-shrink-0
`;

const Logo = tw.img`
   h-8 sm:h-8 md:h-10 lg:h-12 xl:h-14
`;

const LocationSelector = tw.div`
    hidden md:flex items-center space-x-2 cursor-pointer flex-shrink-0 mx-2
`;

const LocationText = tw.span`
    text-sm lg:text-lg font-semibold truncate max-w-[200px] lg:max-w-[300px] text-white
`;

// Desktop Search (hidden on mobile)
const SearchContainer = tw.div`
    hidden md:flex items-center justify-center flex-1 max-w-3xl mx-2 sm:mx-4
`;

const SearchWrapper = tw.div`
    relative flex items-center w-full
`;

const SearchInput = tw.input`
    w-full pl-10 pr-4 py-2 border-2 border-gray-200 bg-gray-100 rounded-lg text-md focus:outline-none
`;

// Mobile Location Row
// const MobileLocationContainer = tw.div`
//     md:hidden px-4 py-2 bg-kuality-blue
// `;
const MobileLocationContainer = tw.div`
    md:hidden px-4 py-2
`;

const MobileLocationSelector = tw.div`
    flex items-center space-x-2 cursor-pointer py-1
`;

const MobileLocationText = tw.span`
    text-sm font-medium text-white truncate flex-1
`;

// Mobile Search Row
const MobileSearchContainer = tw.div`
    md:hidden px-4 pb-3
`;

const MobileSearchWrapper = tw.div`
    relative flex items-center w-full
`;

const NavActions = tw.div`
    flex items-center space-x-3 sm:space-x-4 lg:space-x-8 flex-shrink-0
`;

const NavButton = tw.div`
    flex flex-col items-center sm:space-x-2 text-white hover:text-gray-200 cursor-pointer transition-colors items-center
`;

const NavButtonText = tw.span`
    sm:block font-medium text-xs md:text-sm truncate max-w-[100px] text-white text-center
`;

// Profile Dropdown
const ProfileDropdownContainer = tw.div`
    relative
`;

const DropdownMenu = tw.div`
    absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50
`;

const UserInfoHeader = tw.div`
    px-4 py-3 border-b border-gray-100
`;

const UserInfoContent = tw.div`
    flex items-center space-x-3
`;

const UserAvatar = tw.div`
    w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center
`;

const UserDetails = tw.div``;

const UserName = tw.div`
    font-medium text-gray-900
`;

const UserPhone = tw.div`
    text-sm text-gray-500
`;

const DropdownMenuItems = tw.div`
    py-2
`;

const DropdownButton = tw.button`
    w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-3
`;

const DropdownButtonDanger = tw.button`
    w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-3
`;

const DropdownDivider = tw.div`
    border-t border-gray-100 mt-2 pt-2
`;

const DropdownLink = tw.a`
    w-full block no-underline text-left px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-3 cursor-pointer 
`;

// Categories Section
const CategoriesContainer = tw.div`
    relative flex items-center bg-gray-100 w-full border-b border-gray-200
`;

const ScrollButton = tw.button`
    absolute z-10 p-1 sm:p-2 bg-white shadow-lg rounded-full border border-gray-200 hover:bg-gray-50 transition-colors
    ${(p) => p.$position === 'left' ? 'left-1 sm:left-2' : 'right-1 sm:right-2'}
`;

const CategoriesScrollContainer = tw.div`
    flex overflow-x-auto px-4 sm:px-6 lg:px-8 py-0 w-full
`;

const CategoriesList = tw.div`
    flex items-start space-x-3 sm:space-x-6 lg:space-x-8 text-gray-600 whitespace-nowrap
`;

const CategoryLink = tw(Link)`
    flex flex-col items-center space-y-1 py-2 sm:py-3 px-2 sm:px-4 relative hover:text-kuality-blue transition-colors flex-shrink-0
    ${(p) => p.$active ? 'text-kuality-blue border-b-2 border-kuality-blue' : 'text-gray-600'}
`;

const CategoryImages = tw.img`
  w-20 h-20 object-cover border border-gray-200 bg-white
`;

// const CategoryText = tw.span`
//     text-xs sm:text-sm lg:text-md font-medium whitespace-normal max-w-20
// `;
const CategoryText = tw.span`
    text-xs sm:text-sm lg:text-md font-medium
`;

export default Navbar;